import {useState} from 'react';
import {ApiError, apiErrorCodes} from '@legit-health/backend-client';
import {
  ImageStepInstructions,
  MultipleImagesUploader,
  ImageValue,
  UserId,
  generateRandomId,
  useIsMobile,
  FILE_MAX_SIZE,
  DiagnosticReportId,
  PathologyId,
  BaseUser
} from '@legit-health/ui';
import CloseIcon from '@mui/icons-material/Close';
import SendIcon from '@mui/icons-material/Send';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  IconButton,
  DialogTitle,
  Typography,
  Alert
} from '@mui/material';
import {TFunction} from 'i18next';
import TemporaryImages from '@/shared/components/diagnosticReport/DiagnosticReportForm/components/TemporaryImages';
import {DIAGNOSIS_SUPPORT} from '@/shared/const/backend';
import useApiMutation from '@/shared/hooks/useApiMutation';
import useImageMaxFileSize from '@/shared/hooks/useImageMaxFileSize';
import useModal from '@/shared/hooks/useModal';
import BottomBar from '../../../../steps/BottomBar';
import DiqaErrors from '../../../../steps/DiqaErrors';
import {AnamnesisQuestionsValue} from '../../../../types';
import mapFormToDiagnosisSupportBody from './utils/mapFormToDiagnosisSupportBody';

export type FormValues = {
  images: ImageValue[];
  bodySite: string;
  patientId: UserId;
  visitIdentifier: string | null;
  anamnesisQuestions: AnamnesisQuestionsValue;
};

export default function SendImages({
  activeStep,
  formValues: initialFormValues,
  onSuccess,
  onBack,
  patient,
  t,
  totalSteps,
  withTemporaryImages
}: {
  activeStep: number;
  formValues: FormValues;
  onBack: () => void;
  onSuccess: (response: {
    id: DiagnosticReportId;
    isConclusive?: boolean;
    firstPathologyId?: PathologyId | null;
  }) => void;
  patient: BaseUser;
  t: TFunction;
  totalSteps: number;
  withTemporaryImages: boolean;
}) {
  const isMobile = useIsMobile();
  const customMaxFileSize = useImageMaxFileSize();
  const modalError = useModal();
  const [formValues, setFormValues] = useState(initialFormValues);
  const [postDiagnosisSupportError, setPostDiagnosisSupportError] = useState<ApiError | null>(null);
  const {mutate: postDiagnosisSupport, isPending} = useApiMutation<{
    id: DiagnosticReportId;
    isConclusive?: boolean;
    firstPathologyId?: PathologyId | null;
  }>();

  async function sendImages() {
    const body = await mapFormToDiagnosisSupportBody(formValues);
    postDiagnosisSupport(
      {
        method: 'POST',
        path: DIAGNOSIS_SUPPORT,
        body
      },
      {
        onSuccess: (response) => onSuccess(response.data),
        onError: (e) => {
          setPostDiagnosisSupportError(e);
          modalError.open();
        }
      }
    );
  }

  const isImageError =
    postDiagnosisSupportError &&
    postDiagnosisSupportError.content.errorCode === apiErrorCodes.medicalDevice.invalidImage;

  function handleTemporaryImagesChange(blobs: Blob[]) {
    if (blobs.length === 0) {
      return;
    }

    setFormValues((f) => ({
      ...f,
      images: blobs.map((blob) => ({
        id: generateRandomId(),
        file: blob,
        crop: {
          x: 0,
          y: 0,
          width: 0,
          height: 0
        }
      }))
    }));
  }

  return (
    <>
      <Box
        sx={{
          maxWidth: 'md',
          mx: 'auto'
        }}
      >
        {!isMobile && withTemporaryImages && (
          <Box
            sx={{
              mb: 4
            }}
          >
            <TemporaryImages
              t={t}
              patient={patient}
              onChange={handleTemporaryImagesChange}
              maxImages={3}
            />
          </Box>
        )}
        <ImageStepInstructions
          dontDrawPrimary={t('ui:diagnosticReport.selectImage.dontDraw.primary')}
          dontDrawSecondary={t('ui:diagnosticReport.selectImage.dontDraw.secondary')}
          combinePrimary={t('ui:diagnosticReport.selectImage.dontCombine.primary')}
          combineSecondary={t('ui:diagnosticReport.selectImage.dontCombine.secondary')}
        />
        <Box
          sx={{
            mt: 2
          }}
        >
          <MultipleImagesUploader
            value={formValues.images}
            getValue={() => formValues.images}
            setValue={(v) =>
              setFormValues((f) => ({
                ...f,
                images: v
              }))
            }
            addAnotherLabel={t('ui:addOtherImage')}
            instructionsLabel={t('ui:diagnosticReport.uploader.instructions')}
            sizeErrorMessage={t('ui:dropzone.error.fileSize', {
              maxSize: `${(customMaxFileSize || FILE_MAX_SIZE) / 1000000} MB`
            })}
            {...(customMaxFileSize && {maxFileSize: customMaxFileSize})}
          />
        </Box>
      </Box>
      <BottomBar
        activeStep={activeStep}
        totalSteps={totalSteps}
        isBackEnabled
        isNextEnabled
        rightButton={
          <Button
            color="secondary"
            onClick={sendImages}
            disableElevation
            disabled={!formValues.images.some((i) => i.file) || isPending}
            size="medium"
            variant="contained"
            endIcon={<SendIcon />}
            data-testid="stepperNextButton"
          >
            {isPending ? t('ui:sending') : t('ui:send')}
          </Button>
        }
        t={t}
        onBack={onBack}
      />
      <Dialog onClose={modalError.close} open={modalError.isOpened} id="imageErrorModal">
        <DialogTitle sx={{m: 0, p: 2}}>
          <Typography variant="h3">Error</Typography>
          <IconButton
            aria-label="close"
            onClick={modalError.close}
            sx={{
              position: 'absolute',
              right: 8,
              top: 8,
              color: 'grey.500'
            }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>
        <DialogContent dividers>
          {isImageError ? (
            <Box>
              <DiqaErrors
                t={t}
                onClickNewImage={modalError.close}
                images={formValues.images}
                apiError={postDiagnosisSupportError}
              />
            </Box>
          ) : (
            <Alert severity="error">{t('shared/common:diagnosticReport.form.serverError')}</Alert>
          )}
        </DialogContent>

        <DialogActions>
          <Button
            onClick={modalError.close}
            sx={{
              textTransform: 'uppercase'
            }}
          >
            {t('shared/common:close')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}
